import { z } from 'zod'

import { ensureRouteData } from '@/app/query-client'
import { basketsQueryOptions } from '@/features/baskets/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { AUDIENCES } from '@/schemas/baskets'

/**
 * - `audience`: the tab, For investors by default.
 * - `tag`: the chip; absent is All. mm-be's own slug pattern, so a hand-edited value that could
 *   never match lands on All instead of an empty list.
 *
 * Every field falls back instead of throwing: a stale or hand-edited value lands on the default
 * view, never an error page.
 */
export const basketsSearchSchema = z.object({
  audience: z.enum(AUDIENCES).default('investor').catch('investor'),
  tag: z
    .string()
    .regex(/^[-a-zA-Z0-9_]{1,60}$/)
    .optional()
    .catch(undefined),
})
export type BasketsSearch = z.infer<typeof basketsSearchSchema>

export const BASKETS_SEARCH_DEFAULTS = { audience: 'investor' } as const

/**
 * The `/baskets` loader. MAIN, awaited: the entry URL's list, which is the whole page. A tab or
 * chip change after that is the screen's own read (no `loaderDeps`, docs/route-loading.md). Home's
 * "Baskets to try" reads the investors' list, so arriving from there is a cache hit.
 */
export async function loadBaskets({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const { audience, tag } = basketsSearchSchema.parse(location.search)
  await ensureRouteData(queryClient, basketsQueryOptions({ audience, tag }))
}
